/**
 * O vínculo de uma Pessoa solta a uma Empresa — o componente da 13.3, extraído na 13.4.
 *
 * Nasceu dentro do painel "Dados do contato" do WhatsApp (`modulos/comunicacao/DadosDoContato.tsx`,
 * 13.3) e saiu de lá porque a tela Pessoas (13.4, P13 do chamado de 21/10) faz **o mesmo** gesto:
 * "Vincular a uma Empresa" numa Pessoa solta da lista. Dois componentes divergiriam no primeiro
 * ajuste — a trava `exigir_solta`, o aviso D-10, a releitura antes do `POST`. Agora os dois
 * hospedeiros montam este, e cada um decide só o que é dele: de onde vem a Pessoa, onde mostrar o
 * desfecho, e o que fazer com o foco.
 *
 * **O que ele faz:**
 *
 * - "Vincular a uma Empresa existente": o `CampoDeEmpresa` da 13.1, sem "Criar nova empresa".
 *   Escolhida a Empresa, ele a lê por `id` (`useEmpresa`) e mostra o aviso D-10 antes do "Vincular":
 *   na Empresa de outra pessoa, o Responsável dela passa a ler a conversa, a dona do número continua
 *   vendo-a, e os dados do contato ficam só com quem vê a Empresa (FR-39, "é responsável, vê");
 * - "Cadastrar Empresa com este contato": a ficha do "+ Empresa" no `ModalDaNovaEmpresa`, com o nome
 *   (no Nome fantasia desde a 13.14a, D-5) e o telefone que o hospedeiro sugere; salva, a **mesma** Pessoa é vinculada a ela; se o
 *   ERP achar a ficha pelo documento, ela vira a escolhida do "Vincular", sem vincular sozinha.
 *
 * **Antes de cada vínculo a Pessoa é relida** (`relerAPessoa`, do hospedeiro — que devolve a Pessoa, e
 * não um booleano): a pergunta é "ela ainda está solta agora?". Desde a 13.19c2 as perguntas vão à
 * lista das Empresas visíveis (`empresa_ids`): "ainda solta" é a lista vazia (`ehSolta`), e "já está
 * nesta Empresa" é a Empresa entre elas (`estaNaEmpresa`).
 *
 * - **Ainda solta**: o `POST` sai com `exigir_solta: true` (D-6 da 13.3);
 * - **já vinculada a esta Empresa**: é sucesso, sem `POST`. É a linha de Pessoa do modal que adotou a
 *   solta da própria conversa (13.4; fecha a DW-375) — o cadastro da linha já a levou para lá;
 * - **vinculada a outra, ou sumiu**: nada sai. Se a Empresa acabou de ser criada pelo modal, o
 *   hospedeiro recebe `empresaCriadaSemOContato` — calada, a ficha nova ficaria órfã sem ninguém
 *   saber por quê.
 *
 * **As recusas terminais** — o `409` ("Este contato ja foi vinculado a outra Empresa.") e o `404` (a
 * outra aba levou o contato para uma Empresa que quem vincula não enxerga) — vão verbatim ao
 * hospedeiro, por `aoRecusar`: tentar de novo daria a mesma resposta, e o hospedeiro relê a Pessoa e
 * a mostra fora da seção, que some com ela. Rede e `5xx` ficam aqui, com "Tentar de novo".
 *
 * **Os textos de tela têm acento e são constantes exportadas**; o painel os reexporta. Este módulo
 * não importa nada de `modulos/comunicacao`, e por isso não há ciclo entre os dois.
 *
 * **Com o Negócio sem Empresa da Pessoa (13.20b2, FR-84)** — o hospedeiro passa `definirNoNegocio`
 * quando quem age é a Responsável de um Negócio sem Empresa dessa Pessoa: a Empresa escolhida (ou
 * cadastrada) é **definida no Negócio**, e não vinculada aqui. O `cadastro` vincula o contato a ela
 * ao consumir o evento da definição (13.20c): nenhum `POST` de vínculo sai daqui, e quem espera o
 * vínculo é o hospedeiro (`aoDefinir`). A ficha nova nasce **sem** a Pessoa no `POST /empresas`
 * (o telefone sugerido é o da Empresa, como sempre): o contato chega a ela pela definição. A recusa
 * da definição — o `409` da Empresa fora do recorte de quem pede — vai verbatim, nada é vinculado,
 * e a escolha fica aberta. A definição vem **antes** das perguntas do vínculo: mesmo com o contato
 * já na Empresa (a linha do modal que o adotou), o Negócio precisa dela.
 */

import { useEffect, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { CampoDeEmpresa, type EmpresaEscolhida } from './CampoDeEmpresa'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'
import { ModalDaNovaEmpresa } from './ModalDaNovaEmpresa'
import {
  type Empresa,
  type Pessoa,
  ehSolta,
  estaNaEmpresa,
  useEmpresa,
  useVincularPessoaAEmpresa,
} from './api'
import './vinculo-da-pessoa-solta.css'

export const VINCULAR_A_EXISTENTE = 'Vincular a uma Empresa existente'
export const VINCULAR = 'Vincular'
export const VINCULANDO = 'Vinculando…'
export const CADASTRAR_EMPRESA_COM_O_CONTATO = 'Cadastrar Empresa com este contato'
export const TENTAR_DE_NOVO = 'Tentar de novo'
/** 13.20b2: o vínculo que passa pelo Negócio sem Empresa da Pessoa — o que vai acontecer. */
export const NOTA_DO_VINCULO_PELO_NEGOCIO =
  'Este contato tem um negócio sem empresa: a empresa escolhida é definida nele, e o contato ' +
  'passa a ser dela em alguns segundos.'

/**
 * A ficha nova nasceu, e o contato já estava noutra Empresa (vinculado noutra aba no meio do
 * caminho): a Empresa criada fica, sem ele, e a tela diz isso.
 */
export const empresaCriadaSemOContato = (nomeDaEmpresa: string): string =>
  `A empresa ${nomeDaEmpresa} foi cadastrada, mas este contato já estava vinculado a outra ` +
  'empresa e continua nela.'

/** O desfecho do vínculo, que o hospedeiro mostra fora da seção (ela some com a Pessoa solta). */
export const contatoVinculadoA = (nomeDaEmpresa: string): string =>
  `Contato vinculado a ${nomeDaEmpresa}.`

/**
 * O aviso de antes do "Vincular" (D-10), pelo Responsável da escolhida (13.11c). A conversa é do
 * número e do cadastro (FR-39 emendado): o Responsável da Empresa passa a lê-la, a dona do número
 * continua vendo-a pelo próprio número, e a Pessoa vinculada segue a Empresa — quem não vê a Empresa
 * deixa de ver os dados do contato. Na Empresa minha ou sem Responsável, nada muda para quem vincula.
 */
export const AVISO_EMPRESA_DE_OUTRO =
  'Esta empresa é de outra pessoa: depois do vínculo, o Responsável dela passa a ler a conversa, ' +
  'a dona do número continua vendo-a, e os dados do contato ficam só com quem vê a empresa.'

/** Os `status` em que tentar de novo daria a mesma resposta. */
const STATUS_TERMINAIS = new Set([404, 409])

/** O aviso de antes do "Vincular" (D-10), ou nenhum. */
function avisoDoVinculo(empresa: Empresa | undefined, eu: string | undefined): string | null {
  // Sem a sessão, "é de outra pessoa" não tem com quem comparar: nenhum aviso, e não o aviso errado.
  if (empresa === undefined || eu === undefined) {
    return null
  }
  if (empresa.responsavel_id !== null && empresa.responsavel_id !== eu) {
    return AVISO_EMPRESA_DE_OUTRO
  }
  return null
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** De onde vem a Empresa do "Vincular a uma Empresa existente": da lista, ou do `id` que o ERP achou. */
type Escolha = { origem: 'lista'; empresa: EmpresaEscolhida | null } | { origem: 'id'; id: string }

/** O vínculo em curso, ou a falha dele com o que é preciso para tentar de novo. */
type Vinculo =
  | { estado: 'nenhum' }
  | { estado: 'vinculando' }
  | { estado: 'falhou'; mensagem: string; empresa: EmpresaEscolhida; terminal?: boolean }

export interface PropriedadesDoVinculo {
  /** A Pessoa solta que se vincula. */
  pessoaId: string
  /** O `id` do campo de Empresa — único na página. */
  idDoCampo: string
  /** Relê a Pessoa no `cadastro` (o lote `GET /pessoas?ids=`); `undefined` se a RLS não a devolve. */
  relerAPessoa: () => Promise<Pessoa | undefined>
  /** O nome sugerido na ficha do modal — vai ao Nome fantasia (13.14a); '' para nenhum. */
  nomeDaFicha: string
  /** O telefone sugerido na ficha do modal, já na grafia da ficha. */
  telefoneDaFicha: string
  /** O vínculo saiu (ou a Pessoa já estava nesta Empresa). */
  aoVincular: (empresa: EmpresaEscolhida) => void | Promise<void>
  /** A recusa terminal, verbatim (ou `empresaCriadaSemOContato`): o hospedeiro a mostra e relê. */
  aoRecusar: (mensagem: string) => void
  /** O vínculo em curso, ou o modal aberto — o painel bloqueia o "Fechar" e o Escape com eles. */
  aoMudarOcupacao?: (estado: { ocupado: boolean; modalAberto: boolean }) => void
  /**
   * O prefixo das classes: o painel monta com `contato` (o desenho da 13.3, em `conversas.css`), a
   * tela Pessoas com o padrão (`vinculo`, em `vinculo-da-pessoa-solta.css`).
   */
  prefixo?: string
  /**
   * 13.20b2: define a Empresa escolhida no Negócio sem Empresa da Pessoa, em vez de vincular — o
   * `cadastro` vincula o contato ao consumir o evento. Rejeita com a recusa do serviço.
   */
  definirNoNegocio?: (empresa: EmpresaEscolhida) => Promise<void>
  /** 13.20b2: a definição deu certo — o hospedeiro espera o vínculo ("vinculando…"). */
  aoDefinir?: (empresa: EmpresaEscolhida) => void
}

export function VinculoDaPessoaSolta({
  pessoaId,
  idDoCampo,
  relerAPessoa,
  nomeDaFicha,
  telefoneDaFicha,
  aoVincular,
  aoRecusar,
  aoMudarOcupacao,
  prefixo = 'vinculo',
  definirNoNegocio,
  aoDefinir,
}: PropriedadesDoVinculo) {
  const { data: sessao } = useSessao()
  const classe = (elemento: string) => `${prefixo}__${elemento}`

  const [escolha, setEscolha] = useState<Escolha>({ origem: 'lista', empresa: null })
  const idEscolhido = escolha.origem === 'lista' ? escolha.empresa?.id : escolha.id
  const leituraDaEscolhida = useEmpresa(idEscolhido)
  const escolhida: EmpresaEscolhida | null =
    escolha.origem === 'lista'
      ? escolha.empresa
      : leituraDaEscolhida.data
        ? { id: leituraDaEscolhida.data.id, nome: nomeExibidoDaEmpresa(leituraDaEscolhida.data) }
        : null
  const avisoAntesDoVinculo = avisoDoVinculo(leituraDaEscolhida.data, sessao?.usuario_id)
  const [vinculo, setVinculo] = useState<Vinculo>({ estado: 'nenhum' })
  const vinculacao = useVincularPessoaAEmpresa()
  const [modalAberto, setModalAberto] = useState(false)
  const [abrindoOModal, setAbrindoOModal] = useState(false)

  const ocupado = vinculo.estado === 'vinculando'
  useEffect(() => {
    aoMudarOcupacao?.({ ocupado, modalAberto })
  }, [aoMudarOcupacao, ocupado, modalAberto])
  /* Desmontado (o hospedeiro fechou, ou a Pessoa sumiu), nada mais está ocupado: sem isto o
     hospedeiro ficaria com o modal "aberto" para sempre — e o Escape e o "Fechar" presos. */
  useEffect(
    () => () => {
      aoMudarOcupacao?.({ ocupado: false, modalAberto: false })
    },
    [aoMudarOcupacao],
  )
  /** A releitura antes do modal que falhou (rede): a tela diz, em vez de não fazer nada. */
  const [falhaDaReleitura, setFalhaDaReleitura] = useState<string | null>(null)

  async function vincular(
    empresa: EmpresaEscolhida,
    { daFichaNova = false }: { daFichaNova?: boolean } = {},
  ) {
    if (vinculo.estado === 'vinculando') {
      return
    }
    setVinculo({ estado: 'vinculando' })
    if (definirNoNegocio !== undefined) {
      /* 13.20b2: define no Negócio sem Empresa — o vínculo vem do `cadastro`, pelo evento. */
      try {
        await definirNoNegocio(empresa)
      } catch (falha) {
        /* A definição recusada (o `409` do recorte, o `404` da Empresa que não chegou): nada foi
           vinculado, a mensagem vai verbatim e a escolha fica aberta — escolhe-se outra. O `403`,
           o `404` e o `409` são terminais (code review, B5): tentar de novo daria o mesmo, e não
           há "Tentar de novo". A ficha nova que nasceu e não foi aceita fica escolhida. */
        setVinculo({
          estado: 'falhou',
          mensagem: mensagemDe(falha),
          empresa,
          terminal: falha instanceof ErroDaApi && [403, ...STATUS_TERMINAIS].includes(falha.status),
        })
        if (daFichaNova) {
          setEscolha({ origem: 'lista', empresa })
        }
        return
      }
      setVinculo({ estado: 'nenhum' })
      setEscolha({ origem: 'lista', empresa: null })
      //: Fora do `try` (B5): um tropeço de quem hospeda não vira "a definição falhou".
      aoDefinir?.(empresa)
      return
    }
    try {
      const atual = await relerAPessoa()
      /* 13.19c2: as perguntas vão à lista das Empresas visíveis, e não a um `empresa_id` único — a
         Pessoa pode estar em várias. */
      if (atual !== undefined && estaNaEmpresa(atual, empresa.id)) {
        // Já está nesta Empresa: a linha de Pessoa do modal adotou a solta (13.4). É sucesso.
        setVinculo({ estado: 'nenhum' })
        setEscolha({ origem: 'lista', empresa: null })
        await aoVincular(empresa)
        return
      }
      if (atual === undefined || !ehSolta(atual)) {
        // Vinculada noutra aba (ou sumiu): o hospedeiro já relê e passa ao modo dela. A Empresa que
        // a ficha acabou de criar fica sem o contato — e a tela diz isso, nomeando-a.
        setVinculo({ estado: 'nenhum' })
        if (daFichaNova) {
          aoRecusar(empresaCriadaSemOContato(empresa.nome))
        }
        return
      }
      await vinculacao.mutateAsync({ pessoaId, empresaId: empresa.id, exigirSolta: true })
      setVinculo({ estado: 'nenhum' })
      setEscolha({ origem: 'lista', empresa: null })
      await aoVincular(empresa)
    } catch (falha) {
      if (falha instanceof ErroDaApi && STATUS_TERMINAIS.has(falha.status)) {
        /* Terminal: o contato já está noutra Empresa (ou numa que quem vincula não enxerga), e
           tentar de novo daria a mesma resposta. A mensagem vai verbatim ao hospedeiro. */
        setVinculo({ estado: 'nenhum' })
        aoRecusar(falha.message)
        await relerAPessoa()
        return
      }
      // Rede ou `5xx`: recuperável, com "Tentar de novo" só do vínculo.
      setVinculo({ estado: 'falhou', mensagem: mensagemDe(falha), empresa })
    }
  }

  async function abrirOModal() {
    if (abrindoOModal) {
      return
    }
    setAbrindoOModal(true)
    setFalhaDaReleitura(null)
    try {
      //: 13.20b2: com o Negócio sem Empresa a ficha nova vai à definição, e não ao vínculo da solta
      //: — não importa se o contato é solto para quem age (o ramo o mostra sem Empresa).
      const atual = definirNoNegocio === undefined ? await relerAPessoa() : undefined
      if (definirNoNegocio !== undefined || (atual !== undefined && ehSolta(atual))) {
        setModalAberto(true)
      }
    } catch (falha) {
      setFalhaDaReleitura(mensagemDe(falha))
    } finally {
      setAbrindoOModal(false)
    }
  }

  return (
    <section className={classe('vinculo')} aria-label={VINCULAR_A_EXISTENTE}>
      <h3 className={classe('subtitulo')}>{VINCULAR_A_EXISTENTE}</h3>
      <CampoDeEmpresa
        id={idDoCampo}
        escolhida={escolhida}
        aoEscolher={(empresa) => {
          setVinculo({ estado: 'nenhum' })
          setEscolha({ origem: 'lista', empresa })
        }}
      />
      {idEscolhido !== undefined && leituraDaEscolhida.isError ? (
        <p className={classe('erro')} role="alert">
          {mensagemDe(leituraDaEscolhida.error)}
        </p>
      ) : null}
      {avisoAntesDoVinculo === null ? null : (
        <p className={classe('nota')}>{avisoAntesDoVinculo}</p>
      )}
      {definirNoNegocio === undefined ? null : (
        <p className={classe('nota')}>{NOTA_DO_VINCULO_PELO_NEGOCIO}</p>
      )}
      {escolhida === null ? null : (
        <button
          type="button"
          className={classe('botao')}
          disabled={vinculo.estado === 'vinculando' || !leituraDaEscolhida.isSuccess}
          onClick={() => void vincular(escolhida)}
        >
          {vinculo.estado === 'vinculando' ? VINCULANDO : VINCULAR}
        </button>
      )}
      <button
        type="button"
        className={classe('acao')}
        disabled={abrindoOModal || vinculo.estado === 'vinculando'}
        onClick={() => void abrirOModal()}
      >
        {CADASTRAR_EMPRESA_COM_O_CONTATO}
      </button>
      {falhaDaReleitura === null ? null : (
        <p className={classe('erro')} role="alert">
          {falhaDaReleitura}
        </p>
      )}
      {vinculo.estado === 'falhou' ? (
        <div className={classe('falha')}>
          <p className={classe('erro')} role="alert">
            {vinculo.mensagem}
          </p>
          {vinculo.terminal === true ? null : (
            <button
              type="button"
              className={classe('acao')}
              onClick={() => void vincular(vinculo.empresa)}
            >
              {TENTAR_DE_NOVO}
            </button>
          )}
        </div>
      ) : null}

      {modalAberto ? (
        <ModalDaNovaEmpresa
          textoInicial={nomeDaFicha}
          telefone={telefoneDaFicha}
          aoCancelar={() => {
            setModalAberto(false)
          }}
          aoEscolher={(empresa) => {
            setModalAberto(false)
            void vincular(empresa, { daFichaNova: true })
          }}
          aoEncontrar={(empresaId) => {
            // O ERP achou a ficha: ela vira a escolhida do "Vincular", sem vincular sozinha.
            setModalAberto(false)
            setVinculo({ estado: 'nenhum' })
            setEscolha({ origem: 'id', id: empresaId })
          }}
        />
      ) : null}
    </section>
  )
}
